"use client";

import { useState } from "react";
import { Image } from "@/components/ui/image";
import { ChevronLeft, ChevronRight, Search } from "@/components/icons";
import { PhotoLightbox } from "@/components/photos/photo-lightbox";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { ListingGalleryPhone } from "./listing-gallery-phone";
import { LISTING_GALLERY_SIZES } from "@/components/features/listings/image-sizes";
import { ProductCardPlaceholder } from "@/components/features/listings/product-card-placeholder";

export interface GalleryPhoto {
  _id: string;
  url: string | null;
  caption?: string;
}

const RAIL_SLOTS = 5;
const STAGE_RATIO = "aspect-[3/2] lg:aspect-[4/3]";
const OVERLAY_CONTROL =
  "absolute bg-card/95 shadow-sm hover:bg-card";

interface ListingGalleryProps {
  media: GalleryPhoto[];
  productTitle?: string;
  productTypeSlug?: string | null;
}

function photoAlt(
  item: GalleryPhoto,
  index: number,
  productTitle?: string,
) {
  if (item.caption) return item.caption;
  const position = index + 1;
  return productTitle
    ? `${productTitle} — photo ${position}`
    : `Product image ${position}`;
}

function toLightboxPhotos(media: GalleryPhoto[], productTitle?: string) {
  return media.map((item, index) => ({
    url: item.url ?? "/placeholder.jpg",
    alt: photoAlt(item, index, productTitle),
  }));
}

function StageArrow({
  direction,
  onClick,
}: {
  direction: "previous" | "next";
  onClick: () => void;
}) {
  const isPrevious = direction === "previous";
  return (
    <IconButton
      variant="outline"
      aria-label={`${isPrevious ? "Previous" : "Next"} photo`}
      onClick={onClick}
      className={cn(
        OVERLAY_CONTROL,
        "top-1/2 -translate-y-1/2 rounded-full",
        isPrevious ? "left-3" : "right-3",
      )}
    >
      {isPrevious ? <ChevronLeft /> : <ChevronRight />}
    </IconButton>
  );
}

function RailThumb({
  item,
  index,
  isActive,
  productTitle,
  onSelect,
}: {
  item: GalleryPhoto;
  index: number;
  isActive: boolean;
  productTitle?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Show photo ${index + 1}`}
      aria-current={isActive}
      className={cn(
        "relative overflow-hidden rounded-control bg-muted transition hover:opacity-80",
        isActive && "ring-2 ring-primary",
      )}
    >
      <Image
        src={item.url ?? "/placeholder.jpg"}
        alt={photoAlt(item, index, productTitle)}
        fill
        className="object-cover"
        sizes="76px"
      />
    </button>
  );
}

interface StageProps {
  media: GalleryPhoto[];
  activeIndex: number;
  productTitle?: string;
  onStep: (delta: number) => void;
  onEnlarge: () => void;
}

function ZoomControl({ onClick }: { onClick: () => void }) {
  return (
    <IconButton
      variant="outline"
      aria-label="Zoom photo"
      aria-hidden="true"
      tabIndex={-1}
      onClick={onClick}
      className={cn(OVERLAY_CONTROL, "right-3 top-3 hidden lg:inline-flex")}
    >
      <Search />
    </IconButton>
  );
}

function StageBackdrop({ url }: { url: string | null | undefined }) {
  return (
    <Image
      src={url ?? "/placeholder.jpg"}
      alt=""
      aria-hidden="true"
      fill
      className="scale-110 object-cover opacity-60 blur-2xl"
      sizes={LISTING_GALLERY_SIZES}
      loading="eager"
    />
  );
}

function GalleryStage({
  media,
  activeIndex,
  productTitle,
  onStep,
  onEnlarge,
}: StageProps) {
  const active: GalleryPhoto | undefined = media[activeIndex];
  if (!active) return null;

  return (
    <figure
      className={cn(
        "relative w-full overflow-hidden rounded-lg bg-surface-2 max-md:hidden",
        STAGE_RATIO,
      )}
    >
      <StageBackdrop key={`${active._id}-backdrop`} url={active.url} />
      <Image
        key={active._id}
        src={active.url ?? "/placeholder.jpg"}
        alt={photoAlt(active, activeIndex, productTitle)}
        fill
        className="object-contain"
        sizes={LISTING_GALLERY_SIZES}
        preload={activeIndex === 0}
        fetchPriority={activeIndex === 0 ? "high" : "auto"}
      />
      <button
        type="button"
        onClick={onEnlarge}
        aria-label="Enlarge photo"
        className="absolute inset-0 cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/20"
      />
      {media.length > 1 ? (
        <>
          <StageArrow direction="previous" onClick={() => onStep(-1)} />
          <StageArrow direction="next" onClick={() => onStep(1)} />
          <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-foreground/70 px-2.5 py-0.5 tabular-nums text-xs text-background">
            {activeIndex + 1} / {media.length}
          </span>
        </>
      ) : null}
      <ZoomControl onClick={onEnlarge} />
    </figure>
  );
}

function GalleryRail({
  media,
  activeIndex,
  productTitle,
  onSelect,
  onOverflow,
}: {
  media: GalleryPhoto[];
  activeIndex: number;
  productTitle?: string;
  onSelect: (index: number) => void;
  onOverflow: (index: number) => void;
}) {
  const overflows = media.length > RAIL_SLOTS;
  const items = media.slice(0, overflows ? RAIL_SLOTS - 1 : RAIL_SLOTS);
  const hiddenCount = media.length - items.length;

  return (
    <div className="hidden grid-rows-5 gap-2.5 lg:grid">
      {items.map((item, index) => (
        <RailThumb
          key={item._id}
          item={item}
          index={index}
          isActive={index === activeIndex}
          productTitle={productTitle}
          onSelect={() => onSelect(index)}
        />
      ))}
      {overflows ? (
        <button
          type="button"
          onClick={() => onOverflow(items.length)}
          aria-label={`View ${hiddenCount} more photos`}
          className="grid place-items-center rounded-control bg-muted text-xs font-semibold text-muted-foreground transition hover:opacity-80"
        >
          +{hiddenCount}
        </button>
      ) : null}
    </div>
  );
}

function EmptyStage({ productTypeSlug }: { productTypeSlug?: string | null }) {
  return (
    <ProductCardPlaceholder
      productTypeSlug={productTypeSlug}
      className={cn(
        "rounded-lg max-md:-mx-4 max-md:aspect-auto max-md:h-48 max-md:w-[calc(100%+2rem)] max-md:rounded-none",
        STAGE_RATIO,
      )}
      iconClassName="size-3/5 max-h-64 max-md:max-h-36"
    />
  );
}

function useGalleryState(count: number) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return {
    activeIndex: Math.min(activeIndex, Math.max(count - 1, 0)),
    lightboxIndex,
    select: setActiveIndex,
    step: (delta: number) =>
      setActiveIndex((current) => (current + delta + count) % count),
    openLightboxAt: (index: number) => {
      setActiveIndex(index);
      setLightboxIndex(index);
    },
    onLightboxIndexChange: (next: number | null) => {
      if (next !== null) setActiveIndex(next);
      setLightboxIndex(next);
    },
  };
}

export function ListingGallery({ media, productTitle, productTypeSlug }: ListingGalleryProps) {
  const gallery = useGalleryState(media.length);
  const { activeIndex, lightboxIndex } = gallery;

  if (!media.length) return <EmptyStage productTypeSlug={productTypeSlug} />;

  const hasRail = media.length > 1;

  return (
    <>
      <div
        className={cn(
          "grid gap-2.5 lg:items-stretch",
          hasRail && "lg:grid-cols-[1fr_76px]",
        )}
      >
        <ListingGalleryPhone
          media={media}
          activeIndex={activeIndex}
          productTitle={productTitle}
          onActiveChange={gallery.select}
          onEnlarge={gallery.openLightboxAt}
        />
        <GalleryStage
          media={media}
          activeIndex={activeIndex}
          productTitle={productTitle}
          onStep={gallery.step}
          onEnlarge={() => gallery.openLightboxAt(activeIndex)}
        />
        {hasRail ? (
          <GalleryRail
            media={media}
            activeIndex={activeIndex}
            productTitle={productTitle}
            onSelect={gallery.select}
            onOverflow={gallery.openLightboxAt}
          />
        ) : null}
      </div>
      <PhotoLightbox
        photos={toLightboxPhotos(media, productTitle)}
        index={lightboxIndex}
        onIndexChange={gallery.onLightboxIndexChange}
        title="Product gallery"
      />
    </>
  );
}
